import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { createFestivo, createMultipleFestivos, updateFestivo, deleteFestivo, fetchFestivos } from '../api';
import { useTenantCode } from './useTenantCode';
import { FestivoImpacto } from '../../types/festivos';


// Hook para obtener todos los festivos
export const useFestivosQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['festivos', tenantCode],
        queryFn: fetchFestivos
    });
};


/**
 * P3 — lo que de verdad pasó al marcar el festivo, en el toast.
 *
 * El recuento sale de la RESPUESTA, no de la previsualización que vio el
 * operador antes de confirmar: entre una y otra puede haberse inscrito alguien,
 * y lo que hay que contar es lo que se canceló.
 *
 * Las clases canceladas invalidan también `eventos`: el calendario los tenía
 * cacheados como activos.
 */
const avisarImpacto = (impacto?: FestivoImpacto) => {
    if (!impacto?.cancelledClasses) return;
    const clases = impacto.cancelledClasses === 1
        ? 'Se ha cancelado 1 clase'
        : `Se han cancelado ${impacto.cancelledClasses} clases`;
    // D6 — un festivo ya no compensa, así que `recoveriesIssued` llega a 0 y la
    // frase decía "se han dado 0 recuperaciones". Se menciona solo si las hubo,
    // que hoy es el caso de los festivos anteriores al cambio todavía en vuelo.
    if (!impacto.recoveriesIssued) {
        toast.info(`${clases} de ese día. No generan recuperación: el centro no abre.`);
        return;
    }
    const recuperaciones = impacto.recoveriesIssued === 1
        ? '1 recuperación'
        : `${impacto.recoveriesIssued} recuperaciones`;
    toast.info(`${clases} de ese día y se han dado ${recuperaciones}.`);
};

const invalidarFestivos = () => {
    queryClient.invalidateQueries({ queryKey: ['festivos'] });
    queryClient.invalidateQueries({ queryKey: ['eventos'] });
};

// Mutaciones para crear festivos llamando a la función correspondiente
export const useCreateFestivoMutation = () => useMutation({
    mutationFn: createFestivo,
    onSuccess: (data) => {
        invalidarFestivos();
        toast.success('Festivo creado exitosamente');
        avisarImpacto(data?.impacto);
    },
    onError: () => {
        toast.error('Error creando el festivo');
    },
});

// Mutaciones para crear festivos llamando a la función correspondiente
export const useCreateMultipleFestivosMutation = () => useMutation({
    mutationFn: createMultipleFestivos,
    onSuccess: (data) => {
        invalidarFestivos();
        toast.success('Festivos creados exitosamente');
        avisarImpacto(data?.impacto);
    },
    onError: () => {
        toast.error('Error creando los festivos');
    },
});


// Mutaciones para actualizar festivos llamando a la función correspondiente
export const useUpdateFestivoMutation = () =>  useMutation({
    mutationFn: updateFestivo,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['festivos'] });
        toast.success('Festivo actualizado exitosamente');
    },
    onError: () => {
        toast.error('Error actualizando el festivo');
    },
});


// Mutaciones para eliminar festivos llamando a la función correspondiente
export const useDeleteFestivoMutation = () => useMutation({
    mutationFn: deleteFestivo,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['festivos'] });
        toast.success('Festivo eliminado exitosamente');
    },
    onError: () => {
        toast.error('Error eliminando el festivo');
    },
});